// Sprint 1 — Onboarding post-registro
import { useEffect } from "react";
import { Briefcase, Dumbbell, GraduationCap, Heart, MoreHorizontal, Sparkles } from "lucide-react";
import { css, cx } from "styled-system/css";
import { cf } from "@/components/brand/sfturnos-styles";
import { useOnboardingWizard } from "@/context/onboarding-wizard-context";
import { RUBRO_SUBOPTIONS } from "@/lib/rubro-catalog";
import { onboardingChoiceCard } from "../onboarding-ui";

const OPTIONS = [
  { id: "Salud y Bienestar", label: "Salud y Bienestar", Icon: Heart },
  { id: "Belleza", label: "Belleza", Icon: Sparkles },
  { id: "Educación", label: "Educación", Icon: GraduationCap },
  { id: "Deporte y Fitness", label: "Deporte y Fitness", Icon: Dumbbell },
  { id: "Consultoría", label: "Consultoría", Icon: Briefcase },
  { id: "Otro", label: "Otro", Icon: MoreHorizontal },
] as const;

const FACILITY_RUBRO = "Deporte y Fitness";

function SubOptionsPicker({ rubro }: { rubro: string }) {
  const { state, dispatch } = useOnboardingWizard();
  const entry = RUBRO_SUBOPTIONS[rubro];
  if (!entry) return null;

  function toggle(id: string) {
    const already = state.subRubro.includes(id);
    if (entry.multiSelect) {
      dispatch({
        type: "SET_SUBRUBRO",
        value: already ? state.subRubro.filter((x) => x !== id) : [...state.subRubro, id],
      });
    } else {
      dispatch({ type: "SET_SUBRUBRO", value: already ? [] : [id] });
    }
  }

  return (
    <div className={css({ mb: "8" })}>
      <p className={css({ fontSize: "sm", fontWeight: "semibold", color: "#0f172a", mb: "1" })}>
        {entry.multiSelect ? "¿Qué ofrecés? Elegí una o más opciones" : "¿Qué ofrecés?"}
      </p>
      <p className={css({ fontSize: "xs", color: "#64748b", mb: "3" })}>
        Un mismo negocio puede ofrecer más de una disciplina (ej: pádel y básquet en el mismo complejo).
      </p>
      <div className={css({ display: "flex", flexWrap: "wrap", gap: "2" })}>
        {entry.options.map((o) => {
          const selected = state.subRubro.includes(o.id);
          return (
            <button
              key={o.id}
              type="button"
              aria-pressed={selected}
              onClick={() => toggle(o.id)}
              className={css({
                px: "4",
                py: "2",
                rounded: "full",
                fontSize: "sm",
                fontWeight: "medium",
                cursor: "pointer",
                borderWidth: "1.5px",
                borderColor: selected ? "token(colors.brand.500)" : "#e2e8f0",
                bg: selected ? "token(colors.brand.50)" : "white",
                color: selected ? "token(colors.brand.700)" : "#64748b",
                transition: "border-color 0.15s, background 0.15s, color 0.15s, transform 0.1s ease-out",
                _active: { transform: "scale(0.96)" },
              })}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function OnboardingStepRubro() {
  const { state, dispatch } = useOnboardingWizard();
  const isFacility = state.accountType === "FACILITY";
  const selected = state.rubro;
  const subEntry = RUBRO_SUBOPTIONS[selected];

  // Una cuenta FACILITY ya declaró "Tengo una instalación" al registrarse: no le repetimos la
  // pregunta genérica de rubro, el rubro queda implícito y pasamos directo a qué deportes ofrece.
  useEffect(() => {
    if (isFacility && state.rubro !== FACILITY_RUBRO) {
      dispatch({ type: "SET_FIELD", field: "rubro", value: FACILITY_RUBRO });
    }
  }, [isFacility, state.rubro, dispatch]);

  function selectRubro(id: string) {
    dispatch({ type: "SET_FIELD", field: "rubro", value: id });
    dispatch({ type: "SET_SUBRUBRO", value: [] });
  }

  const canContinue = isFacility ? state.subRubro.length > 0 : Boolean(selected) && (!subEntry || state.subRubro.length > 0);

  return (
    <div>
      <h1 className={css({ fontSize: "2xl", fontWeight: "bold", color: "#0f172a", mb: "2" })}>
        Creando tu primera agenda
      </h1>

      {isFacility ? (
        <>
          <p className={css({ fontSize: "sm", color: "#64748b", mb: "8", maxW: "42rem", lineHeight: 1.6 })}>
            Ya sabemos que tu cuenta es una instalación. Contanos qué deportes ofrecés para poder sugerirte tus
            canchas más adelante.
          </p>
          <SubOptionsPicker rubro={FACILITY_RUBRO} />
        </>
      ) : (
        <>
          <p className={css({ fontSize: "sm", color: "#64748b", mb: "2", maxW: "42rem", lineHeight: 1.6 })}>
            ¿Para qué rubro es?
          </p>
          <p className={css({ fontSize: "sm", color: "#64748b", mb: "8", maxW: "42rem", lineHeight: 1.6 })}>
            Si no ves tu rubro, seleccioná &apos;Otro&apos;. El sistema sirve para todos los rubros.
          </p>

          <div
            className={css({
              display: "grid",
              gridTemplateColumns: { base: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
              gap: "4",
              mb: "8",
            })}
          >
            {OPTIONS.map(({ id, label, Icon }) => {
              const isSel = selected === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => selectRubro(id)}
                  className={cx(
                    onboardingChoiceCard({ selected: isSel }),
                    css({ border: "none", font: "inherit" }),
                  )}
                >
                  <Icon
                    className={css({
                      w: "7",
                      h: "7",
                      color: isSel ? "token(colors.brand.500)" : "#64748b",
                      flexShrink: 0,
                    })}
                    strokeWidth={1.75}
                  />
                  <span className={css({ fontWeight: "semibold", fontSize: "sm", color: "#0f172a" })}>{label}</span>
                </button>
              );
            })}
          </div>

          {selected && subEntry ? <SubOptionsPicker rubro={selected} /> : null}
        </>
      )}

      {canContinue ? (
        <button
          type="button"
          onClick={() => dispatch({ type: "NEXT_STEP" })}
          className={cx(
            cf.linkPrimaryOnDark,
            css({
              px: "6",
              py: "3",
              border: "none",
              fontSize: "sm",
              cursor: "pointer",
              bg: "linear-gradient(90deg, token(colors.brand.600), token(colors.brand.500))",
            }),
          )}
        >
          Continuar
        </button>
      ) : null}
    </div>
  );
}
